import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { BRAND } from "@/lib/brand";
import { Menu, X, Salad } from "lucide-react";
import { LanguageSwitcher } from "@/components/site/LanguageSwitcher";

export const Header = () => {
    const { t } = useTranslation();
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);

    const navLinks = [
        { label: t("header.para_quem"), href: "#para-quem" },
        { label: t("header.como_funciona"), href: "#como-funciona" },
        { label: t("header.beneficios"), href: "#beneficios" },
        { label: t("header.testemunhos"), href: "#testemunhos" },
        { label: t("header.faq"), href: "#faq" },
    ];

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    const handleScrollTo = (href) => {
        setOpen(false);
        const el = document.querySelector(href);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    };

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
                scrolled
                    ? "bg-white/80 backdrop-blur-md border-b border-stone-200/70"
                    : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 md:px-12 h-24 flex items-center justify-between">
                <a
                    href="#top"
                    onClick={(e) => {
                        e.preventDefault();
                        window.scrollTo({ top: 0, behavior: "smooth" });
                    }}
                    className="flex items-center gap-3"
                    data-testid="header-logo-link"
                >
                    <img
                        src={BRAND.logo}
                        alt="Synara Alvarenga"
                        className="h-16 w-16 sm:h-20 sm:w-20 object-contain"
                    />
                    <div className="leading-tight">
                        <div className="font-serif text-base sm:text-lg text-stone-900">
                            {BRAND.name}
                        </div>
                        <div className="text-[9px] sm:text-[10px] uppercase tracking-[0.22em] text-stone-500">
                            {BRAND.tagline}
                        </div>
                    </div>
                </a>

                <nav className="hidden md:flex items-center gap-10">
                    {navLinks.map((l) => (
                        <button
                            key={l.href}
                            onClick={() => handleScrollTo(l.href)}
                            className="text-sm text-stone-700 hover:text-stone-900 link-underline transition-colors"
                            data-testid={`nav-${l.href.replace("#", "")}`}
                        >
                            {l.label}
                        </button>
                    ))}
                </nav>

                <div className="flex items-center gap-3">
                    <Link
                        to="/marmitas"
                        className="hidden lg:inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-stone-600 hover:text-[#a4632f] transition-colors px-3 py-2 rounded-full ring-1 ring-stone-300/70 hover:ring-[#c67b3f]/60 hover:bg-[#faf5ea]"
                        data-testid="header-crossnav-marfit"
                    >
                        <Salad size={13} strokeWidth={1.6} />
                        MarFit Meals
                    </Link>
                    <LanguageSwitcher />
                    <Button
                        onClick={() => handleScrollTo("#quiz")}
                        className="hidden sm:inline-flex rounded-full bg-stone-900 hover:bg-stone-800 text-stone-50 px-6 py-5 text-sm"
                        data-testid="header-cta-button"
                    >
                        {t("header.cta")}
                    </Button>
                    <button
                        className="md:hidden p-2 -mr-2"
                        onClick={() => setOpen((v) => !v)}
                        data-testid="mobile-menu-toggle"
                        aria-label={t("header.menu_aria")}
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            {/* Mobile menu */}
            {open && (
                <div
                    className="md:hidden bg-white/95 backdrop-blur-md border-b border-stone-200"
                    data-testid="mobile-menu"
                >
                    <div className="px-6 py-6 flex flex-col gap-5">
                        {navLinks.map((l) => (
                            <button
                                key={l.href}
                                onClick={() => handleScrollTo(l.href)}
                                className="text-left text-stone-800 text-base"
                                data-testid={`mobile-nav-${l.href.replace("#", "")}`}
                            >
                                {l.label}
                            </button>
                        ))}
                        <Link
                            to="/marmitas"
                            onClick={() => setOpen(false)}
                            className="inline-flex items-center gap-2 text-[#a4632f] text-base border-t border-stone-200 pt-5"
                            data-testid="mobile-crossnav-marfit"
                        >
                            <Salad size={16} strokeWidth={1.6} />
                            Ver MarFit Meals
                        </Link>
                        <Button
                            onClick={() => handleScrollTo("#quiz")}
                            className="rounded-full bg-stone-900 hover:bg-stone-800 text-stone-50"
                            data-testid="mobile-header-cta"
                        >
                            {t("header.cta")}
                        </Button>
                    </div>
                </div>
            )}
        </header>
    );
};
